import * as React from 'react';
import { IModalProps, Modal } from '../modal';
import { CrossCloseModalButton } from './common-close-modal-buttons';
import './common-modal.scss';
import { CommonPopupLayout } from '../../common-popup/common-popup';
import classnames from 'classnames';

interface ICommonModalProps {
    /**
     * if set - specify title to be shown in the popup hedaer
     */
    title?: string | React.ReactNode;
    /**
     * Render specified node in footer of the popup. Footer is only rendered if this property is set.
     * Content of the footer can be fully customizable
     */
    footer?: React.ReactNode;
    /**
     * popup is visible as long as the property is set to true
     */
    isOpen: boolean;
    /**
     * define an action which happens when user either hit one of the 'close' buttons inside the modal or press 'esc' button,
     * or click outside of modal. If not set - 'close' cross is not rendered
     */
    onRequestClose?: (event: React.MouseEvent | React.KeyboardEvent) => void;
    /**
     * let suppress rendering cross icon even if 'onRequestClose' is provided.
     * Must be used 'ONLY' if cancel button, other than 'cross' is provided.
     */
    supressRenderingCrossIcon?: boolean;
    /**
     * for custom styling, added to the root 'div' of the modal window
     */
    className?: string;
    classNameBody?: string;
    /**
     * Custom class applied to the modal overlay (passed through to ReactModal).
     */
    overlayClassName?: string;
    isWide?: boolean;
    isExtraWide?: boolean;
    /**
     * when true - opening this modal does NOT hide whichever modal is currently on top of the stack.
     * use for a modal which is meant to visually stack on top of another modal instead of replacing it.
     */
    keepPreviousModalVisible?: boolean;
    /**
     * exposes the modal's content DOM node, e.g. so another (stacked) modal can measure/position itself against it.
     */
    contentRef?: (instance: HTMLDivElement) => void;
}

/**
 * Common modal for OEConnect application
 * utilize common popups layout (see .oe-popup-layout class as layout root)
 * @param props
 * @constructor
 */
const CommonModal = (props : React.PropsWithChildren<ICommonModalProps>) => {
    const className = 'oe-popup-layout oe-popup-layout--modal' + classnames({
        ' oe-popup-layout--modal-wide ': props.isWide,
        ' oe-popup-layout--modal-extra-wide ': props.isExtraWide,
    }) + (props.className ? ' ' + props.className : '');

    const finalProps: IModalProps = {
        ...props,
        className: className,
    };

    const shallRenderCloseIcon = props.onRequestClose && !props.supressRenderingCrossIcon;
    const showHeader = props.title || shallRenderCloseIcon;

    return (
        <Modal
            {...finalProps}
        >
            <CommonPopupLayout
                body={props.children}
                footer={props.footer}
                header={showHeader && (
                    <>
                        {shallRenderCloseIcon &&
                            <CrossCloseModalButton />
                        }
                        {props.title &&
                            <span className='oe-popup-layout__header-title'>
                                {props.title}
                            </span>
                        }
                    </>
                )}
                classNameBody={props.classNameBody}
            />
        </Modal>
    );
};

export { CommonModal, ICommonModalProps };
